import React, {
	createContext,
	useContext,
	useReducer,
	useEffect,
	useState,
	ReactNode,
	Dispatch,
} from 'react';

import Loader from './Components/Loader';
import NoChatbotIdComponent from './Components/NoChatbotIdComponent';
import DisabledApplication from './Components/DisabledApplication';

import { getSettings } from './Components/Api/getSettings';
import { getMessages } from './Components/Api/getMessages';
import { getListTypeApplication } from './Components/Api/getListTypeApplication';
import { extractUUID } from './utils/uuidfinder';

export enum ActionTypes {
	SetChatbotSettings = 'SET_CHATBOT_SETTINGS',
	ToggleChatbotVisibility = 'TOGGLE_CHATBOT_VISIBILITY',
	SetChatbotShortcuts = 'SET_CHATBOT_SHORTCUTS',
	SetListTypeApplication = 'SET_LIST_TYPE_APPLICATION',
	SetDemoTheme = 'SET_DEMO_THEME',
	SetAPIError = 'SET_API_ERROR',
}

interface GlobalState {
	chatbotSettings: any;
	chatbotShortcuts: any;
	chatbotTypeList: any;
	isLoadingChatbotShortcuts: any;
	isLoadingChatbotSettings: boolean;
	isChatbotVisible: boolean;
	setDemoTheme: boolean;
	setAPIError: boolean;
}

interface SetChatbotSettingsAction {
	type: ActionTypes.SetChatbotSettings;
	payload: any;
}

interface SetChatbotShortcutsAction {
	type: ActionTypes.SetChatbotShortcuts;
	payload: any;
}

interface SetListTypeApplicationAction {
	type: ActionTypes.SetListTypeApplication;
	payload: any;
}

interface ToggleChatbotVisibilityAction {
	type: ActionTypes.ToggleChatbotVisibility;
}

interface SetDemoTheme {
	type: ActionTypes.SetDemoTheme;
	payload: boolean;
}

interface SetAPIError {
	type: ActionTypes.SetAPIError;
	payload: boolean;
}

type GlobalAction =
	| SetChatbotSettingsAction
	| ToggleChatbotVisibilityAction
	| SetChatbotShortcutsAction
	| SetListTypeApplicationAction
	| SetDemoTheme
	| SetAPIError;

const GlobalStateContext = createContext<
	| {
		state: GlobalState;
		dispatch: Dispatch<GlobalAction>;
	}
	| undefined
>(undefined);

const initialState: GlobalState = {
	chatbotSettings: null,
	chatbotShortcuts: null,
	chatbotTypeList: null,
	isLoadingChatbotSettings: true,
	isLoadingChatbotShortcuts: true,
	isChatbotVisible: true,
	setDemoTheme: false,
	setAPIError: false,
};

const globalReducer = (
	state: GlobalState,
	action: GlobalAction,
): GlobalState => {
	switch (action.type) {
	case ActionTypes.SetChatbotSettings:
		return {
			...state,
			chatbotSettings: action.payload,
			isLoadingChatbotSettings: false,
		};
	case ActionTypes.SetChatbotShortcuts:
		return {
			...state,
			chatbotShortcuts: action.payload,
			isLoadingChatbotShortcuts: false,
		};
	case ActionTypes.SetListTypeApplication:
		return {
			...state,
			chatbotTypeList: action.payload,
		};
	case ActionTypes.SetDemoTheme:
		return {
			...state,
			setDemoTheme: action.payload,
		};
	case ActionTypes.ToggleChatbotVisibility:
		return {
			...state,
			isChatbotVisible: !state.isChatbotVisible,
		};
	case ActionTypes.SetAPIError:
		return {
			...state,
			setAPIError: action.payload,
		};
	default:
		return state;
	}
};

const GlobalStateProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [state, dispatch] = useReducer(globalReducer, initialState);
	const [settingsId, setSettingsId] = useState('');
	const [appType, setAppType] = useState('');
	const urlSearchParams = new URLSearchParams(window.location.search);

	const chatbotId = extractUUID(
		urlSearchParams.get('chatbot_id') ||
		urlSearchParams.get('application_id')
	);

	const demo = urlSearchParams.get('demo');

	const getTypeApp = (appType: string) => {
		switch (appType) {
		case 'AgentApplication':
			return 'agent_app';
		case 'FlowApplication':
			return 'flow_app';
		case 'SearchApplication':
		default:
			return 'search_app';
		}
	};

	const fetchSettings = async () => {
		try {
			if (chatbotId) {
				getSettings(chatbotId).then((response) => {
					const appTypeName = getTypeApp(response.app_type);
					setAppType(appTypeName);
					setSettingsId(response.id);
					dispatch({
						type: ActionTypes.SetChatbotSettings,
						payload: response,
					});
				});
			} else {
				console.error(
					'No se encontró el parámetro chatbot_id en la URL',
				);
				dispatch({
					type: ActionTypes.SetChatbotSettings,
					payload: null,
				});
				dispatch({
					type: ActionTypes.SetAPIError,
					payload: true,
				});
			}
		} catch (error) {
			console.error('Error fetching data:', error);
		}
	};

	const fetchShortcuts = async (chatbotId: string) => {
		try {
			getMessages(chatbotId).then((response) => {
				// console.log(response);
				dispatch({
					type: ActionTypes.SetChatbotShortcuts,
					payload: response.payload,
				});
			});
		} catch (error) {
			console.error('Error fetching data:', error);
		}
	};

	const fetchListTypeApplication = async () => {
		try {
			getListTypeApplication(chatbotId, appType).then((response) => {
				dispatch({
					type: ActionTypes.SetListTypeApplication,
					payload: response.payload,
				});
			});
		} catch (error) {
			dispatch({
				type: ActionTypes.SetChatbotSettings,
				payload: null,
			});
			dispatch({
				type: ActionTypes.SetAPIError,
				payload: true,
			});
			console.error('Error fetching data:', error);
		}
	};

	useEffect(() => {
		fetchSettings();
	}, []);

	useEffect(() => {
		if (appType) {
			fetchListTypeApplication();
		}
	}, [appType]);

	useEffect(() => {
		if (settingsId !== '') {
			fetchShortcuts(settingsId);
		}
	}, [settingsId]);

	useEffect(() => {
		if (demo === 'true') {
			dispatch({
				type: ActionTypes.SetDemoTheme,
				payload: true,
			});
		}
	}, []);

	// if (state.isLoadingChatbotSettings || state.isLoadingChatbotShortcuts) {
	// 	return <Loader />;
	// }
	// if (!state.chatbotSettings) {
	// 	return <NoChatbotIdComponent />;
	// }
	// if (state.chatbotSettings) {
	// 	const booleanIsPublic =
	// 		typeof state.chatbotSettings.is_public === 'string'
	// 			? state.chatbotSettings.is_public === 'True'
	// 			: state.chatbotSettings.is_public;
	// 	if (!booleanIsPublic) {
	// 		return <DisabledApplication />;
	// 	}
	// }

	return (
		<GlobalStateContext.Provider value={{ state, dispatch }}>
			{children}
		</GlobalStateContext.Provider>
	);
};

const useGlobalState = (): {
	state: GlobalState;
	dispatch: Dispatch<GlobalAction>;
} => {
	const context = useContext(GlobalStateContext);
	if (!context) {
		throw new Error(
			'useGlobalState must be used within a GlobalStateProvider',
		);
	}
	return context;
};

export { GlobalStateProvider, useGlobalState };
